import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, Trash2, Copy, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context";
import http from "@/api";
import copyToClipboard from "@/lib/copyToClipboard";

/**
 * RARE GROUP AI Chat — ChatGPT-style assistant.
 * - Claude Sonnet 4.5 backend with live store context
 * - Markdown bold/italic/lists rendered inline (lightweight, no extra deps)
 * - Suggestion chips on first open + after every assistant reply
 * - Persistent session in localStorage so conversations survive refresh
 * - Copy-to-clipboard per message; clear-chat button
 */
export default function Chatbot({ embedded = false, onClose, onHandoff }) {
  const { t, lang, country, addToCart, cart, clearCart, currency, user } = useApp();
  const [open, setOpen] = useState(false);
  const [rated, setRated] = useState(() => localStorage.getItem("rg_chat_rated") === "1");
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("rg_chat_sid") || null);
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(localStorage.getItem("rg_chat_msgs") || "[]"); } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [ckOpen, setCkOpen] = useState(false);
  const [ckForm, setCkForm] = useState({ name: "", phone: "", address: "", payment: "cash" });
  const [placing, setPlacing] = useState(false);
  const isOpen = embedded || open;
  const close = () => (embedded ? onClose?.() : setOpen(false));
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Welcome message on first open of a fresh session
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: "assistant",
        text: lang === "ar"
          ? "أهلاً! أنا **RARE GROUP**، مساعدك الذكي.\n\nأقدر أساعدك في:\n- اقتراح هدايا للمناسبات 🎁\n- كتابة عبارات تهنئة 💌\n- شرح أنواع الورد والمنتجات 🌹\n- طرق الدفع والتوصيل 🚚\n- أو أي سؤال آخر تحبه ✨\n\nكيف أقدر أخدمك اليوم؟"
          : "Hi! I'm **RARE GROUP**, your smart assistant.\n\nI can help with:\n- Gift suggestions 🎁\n- Greeting card wording 💌\n- Product info 🌹\n- Payment & delivery 🚚\n- Or any other question ✨\n\nHow can I help today?"
      }]);
    }
  // eslint-disable-next-line
  }, [isOpen]);

  // Persist
  useEffect(() => {
    try { localStorage.setItem("rg_chat_msgs", JSON.stringify(messages.slice(-50))); } catch { /**/ }
  }, [messages]);
  useEffect(() => {
    if (sessionId) localStorage.setItem("rg_chat_sid", sessionId);
  }, [sessionId]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // Auto-focus input when opened
  useEffect(() => { if (isOpen) setTimeout(() => inputRef.current?.focus(), 200); }, [isOpen]);

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await http.post("/ai/chat", { session_id: sessionId, message: text, language: lang, country });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply, handoff: !!data.handoff, products: data.products || [] }]);
      if (data.handoff && onHandoff) setTimeout(() => onHandoff(), 1500);
    } catch {
      setMessages((m) => [...m, {
        role: "assistant",
        text: lang === "ar" ? "عذراً، حصل خطأ. حاول مرة ثانية." : "Sorry, an error occurred. Try again."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    if (!window.confirm(lang === "ar" ? "مسح المحادثة؟" : "Clear conversation?")) return;
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem("rg_chat_sid");
    localStorage.removeItem("rg_chat_msgs");
  };

  const copyMessage = async (text, idx) => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(idx);
      setTimeout(() => setCopied(null), 1500);
    }
  };

  // 🤩 مقترحات تكميلية فورية بعد الإضافة للسلة (شوكولاتة/بالونات/كروت)
  const suggestComplement = async (pid) => {
    try {
      const { data } = await http.get(`/ai/chat/complement?product_id=${pid}&country=${country}`);
      const inCart = new Set((cart || []).map((c) => c.product_id));
      const prods = (data.products || []).filter((p) => !inCart.has(p.id) && p.id !== pid).slice(0, 2);
      if (!prods.length || !data.message) return;
      setMessages((m) => [...m, {
        role: "assistant",
        text: lang === "ar" ? data.message : (data.message_en || data.message),
        products: prods, complement: true,
      }]);
    } catch { /* noop */ }
  };

  // 🛒 إتمام الطلب من داخل الشات
  const placeChatOrder = async () => {
    if (ckForm.name.trim().length < 2 || ckForm.phone.replace(/\D/g, "").length < 8 || ckForm.address.trim().length < 5) {
      toast.error(lang === "ar" ? "أكمل الاسم والجوال والعنوان" : "Fill name, phone and address");
      return;
    }
    setPlacing(true);
    try {
      let branchId = null;
      try {
        const { data: brs } = await http.get("/branches");
        const match = (brs || []).find((b) => b.is_active !== false && (b.country_code || "QA") === country);
        branchId = match?.id || null;
      } catch { /* backend falls back */ }
      const { data } = await http.post("/orders/checkout", {
        client_key: (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`),
        items: cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant_id: i.variant_id || null,
                                  selected_options: i.selected_options || null, custom_color: i.custom_color || null })),
        currency,
        delivery_address: ckForm.address,
        recipient_name: ckForm.name,
        recipient_phone: ckForm.phone,
        branch_id: branchId,
        payment_method: ckForm.payment === "card" ? "card" : "cash",
        guest_name: user ? "" : ckForm.name,
        guest_phone: user ? "" : ckForm.phone,
        sender_phone: ckForm.phone,
      });
      const order = data.order;
      let payUrl = "";
      if (ckForm.payment === "card") {
        try {
          const { data: pay } = await http.post("/payments/myfatoorah/init", { order_id: order.id });
          payUrl = pay?.invoice_url || "";
          if (payUrl) window.open(payUrl, "_blank");
        } catch { /* fallback button below */ }
      }
      clearCart();
      setCkOpen(false);
      const code = (order.code || "").toUpperCase();
      setMessages((m) => [...m, {
        role: "assistant",
        order_code: order.code, pay_url: payUrl,
        text: ckForm.payment === "card"
          ? (lang === "ar"
              ? `تم إنشاء طلبك **${code}** ✓\nفتحت لك صفحة الدفع الآمنة 🔒 — بعد إتمام الدفع نبدأ التجهيز فوراً 🌹`
              : `Order **${code}** created ✓ — secure payment page opened 🔒 We start preparing right after payment 🌹`)
          : (lang === "ar"
              ? `تم استلام طلبك **${code}** ✓\nالدفع عند الاستلام 💵 والتوصيل مجاني — بنتواصل معك للتأكيد 🌹`
              : `Order **${code}** received ✓ — cash on delivery & free shipping 🌹`),
      }]);
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || e.message));
    } finally {
      setPlacing(false);
    }
  };

  // Quick suggestion chips (shown when chat is empty or short)
  const suggestions = lang === "ar" ? [
    { icon: "🎂", text: "اقترح هدية لعيد ميلاد زوجتي" },
    { icon: "💐", text: "أحلى أنواع الورد عندكم؟" },
    { icon: "💌", text: "اكتب لي عبارة تهنئة بالخطوبة" },
    { icon: "🚚", text: "كم يستغرق التوصيل في الدوحة؟" },
  ] : [
    { icon: "🎂", text: "Birthday gift idea for my wife" },
    { icon: "💐", text: "Best roses you have?" },
    { icon: "💌", text: "Write me an engagement greeting" },
    { icon: "🚚", text: "How fast is delivery in Doha?" },
  ];
  const showSuggestions = messages.length <= 1 && !loading;

  const inner = (
    <>
              {/* Messages */}
              <div ref={scrollRef}
                   className="flex-1 overflow-y-auto p-4 space-y-3"
                   style={{ background: "var(--bg-subtle)" }}
                   data-testid="chatbot-messages">
                {messages.map((m, i) => (
                  <div key={m.id || `${m.role}-${i}-${m.ts || ""}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} group`}>
                    <div className={`max-w-[85%] relative ${m.role === "user" ? "" : "flex gap-2 items-start"}`}>
                      {m.role === "assistant" && (
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5"
                             style={{ background: "var(--primary)", color: "var(--secondary)" }}>
                          F
                        </div>
                      )}
                      <div className={`p-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${m.role === "user" ? "rounded-br-md" : "rounded-bl-md"}`}
                           style={m.role === "user"
                             ? { background: "var(--primary)", color: "#fff" }
                             : { background: "#fff", color: "var(--text-main)", border: "1px solid var(--border)" }}>
                        <Markdown text={m.text} />
                        {m.role === "assistant" && (m.products || []).length > 0 && (
                          <div className="mt-2 space-y-2" data-testid={`chat-products-${i}`}>
                            {m.products.map((p) => (
                              <div key={p.id}
                                   className="flex items-center gap-2.5 rounded-xl border p-2 bg-white"
                                   style={{ borderColor: "var(--border)" }}
                                   data-testid={`chat-product-${p.id}`}>
                                <Link to={`/p/${p.id}`} onClick={close} className="flex items-center gap-2.5 flex-1 min-w-0">
                                  {p.image ? (
                                    <img src={p.image.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${p.image}` : p.image}
                                         alt="" className="w-14 h-14 rounded-lg object-cover shrink-0"
                                         onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                  ) : (
                                    <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">🌸</div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-bold truncate" style={{ color: "var(--text-main)" }}>
                                      {lang === "ar" ? p.name_ar : (p.name_en || p.name_ar)}
                                    </div>
                                    <div className="text-[11px] font-bold mt-0.5" style={{ color: "var(--primary)" }}>
                                      {Number(p.price_sar).toFixed(0)} {p.currency}
                                    </div>
                                  </div>
                                </Link>
                                <button onClick={() => {
                                          addToCart(p, 1);
                                          toast.success(lang === "ar" ? `أُضيف للسلة ✓ ${p.name_ar || ""}` : "Added to cart ✓");
                                          if (!m.complement) suggestComplement(p.id);
                                        }}
                                        className="shrink-0 flex items-center gap-1 rounded-full px-3 py-1.5 text-[10.5px] font-bold text-white active:scale-95 transition-transform"
                                        style={{ background: "var(--primary)" }}
                                        data-testid={`chat-add-cart-${p.id}`}>
                                  <Plus size={12} /> {lang === "ar" ? "أضف للسلة" : "Add to cart"}
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {m.role === "assistant" && m.order_code && (
                          <div className="mt-2 flex gap-2 flex-wrap" data-testid={`chat-order-actions-${i}`}>
                            <Link to={`/track/${m.order_code}`} onClick={close}
                                  className="chip !text-[10.5px] font-bold" data-testid="chat-track-order">
                              📦 {lang === "ar" ? "تتبع طلبك" : "Track order"}
                            </Link>
                            {m.pay_url && (
                              <a href={m.pay_url} target="_blank" rel="noreferrer"
                                 className="chip !text-[10.5px] font-bold" data-testid="chat-pay-link">
                                🔒 {lang === "ar" ? "أكمل الدفع" : "Complete payment"}
                              </a>
                            )}
                          </div>
                        )}
                        {m.role === "assistant" && m.text && (
                          <button onClick={() => copyMessage(m.text, i)}
                                  className="absolute -bottom-2 -end-2 w-7 h-7 rounded-full bg-white shadow-md opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                  data-testid={`chat-copy-${i}`}>
                            {copied === i ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {/* تقييم الرضا — يظهر بعد 3 ردود وحتى أول تقييم */}
                {!rated && sessionId && messages.filter((m) => m.role === "assistant").length >= 3 && !loading && (
                  <div className="flex justify-start" data-testid="chat-rating-row">
                    <div className="flex items-center gap-2 text-[11px] opacity-80 ps-9">
                      <span>{lang === "ar" ? "هل كان المساعد مفيداً؟" : "Was this helpful?"}</span>
                      {[["5", "👍"], ["1", "👎"]].map(([r, icon]) => (
                        <button key={r}
                                onClick={async () => {
                                  try {
                                    await http.post("/cs/rate", { session_id: sessionId, rating: Number(r), channel: "web" });
                                    setRated(true); localStorage.setItem("rg_chat_rated", "1");
                                  } catch { /* noop */ }
                                }}
                                className="w-8 h-8 rounded-full bg-white border shadow-sm hover:scale-110 transition-transform"
                                style={{ borderColor: "var(--border)" }}
                                data-testid={`chat-rate-${r}`}>
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {loading && (
                  <div className="flex justify-start">
                    <div className="flex gap-2 items-start">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5"
                           style={{ background: "var(--primary)", color: "var(--secondary)" }}>
                        F
                      </div>
                      <div className="p-3 rounded-2xl bg-white border text-sm flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-gray-400 pulse-dot" />
                        <span className="w-2 h-2 rounded-full bg-gray-400 pulse-dot" style={{ animationDelay: "0.2s" }} />
                        <span className="w-2 h-2 rounded-full bg-gray-400 pulse-dot" style={{ animationDelay: "0.4s" }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Suggestion chips */}
              {showSuggestions && (
                <div className="px-3 pt-2 pb-1 flex gap-2 overflow-x-auto scroll-x-hide"
                     data-testid="chat-suggestions">
                  {suggestions.map((s) => (
                    <button key={s.text} onClick={() => send(s.text)}
                            className="shrink-0 text-xs px-3 py-1.5 rounded-full border whitespace-nowrap hover:bg-black/5 transition-colors flex items-center gap-1.5"
                            style={{ borderColor: "var(--border)" }}>
                      <span>{s.icon}</span> {s.text}
                    </button>
                  ))}
                </div>
              )}

              {/* 🛒 إتمام الطلب من الشات */}
              {(cart || []).length > 0 && !ckOpen && (
                <button onClick={() => {
                          setCkForm((f) => ({ ...f, name: f.name || user?.name || "", phone: f.phone || user?.phone || "" }));
                          setCkOpen(true);
                        }}
                        className="mx-3 mb-1 rounded-xl py-2.5 text-xs font-bold text-white shadow-sm"
                        style={{ background: "var(--primary)" }}
                        data-testid="chat-checkout-open">
                  🛒 {lang === "ar" ? `أكمل طلبك من هنا (${cart.length} ${cart.length === 1 ? "منتج" : "منتجات"})` : `Checkout here (${cart.length})`}
                </button>
              )}
              {ckOpen && (
                <div className="border-t p-3 space-y-2 bg-white max-h-80 overflow-y-auto"
                     style={{ borderColor: "var(--border)" }} data-testid="chat-checkout-panel">
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-bold flex-1">
                      🛒 {lang === "ar" ? `إتمام الطلب (${cart.length}) — التوصيل مجاني 🌹` : `Checkout (${cart.length}) — free delivery 🌹`}
                    </div>
                    <button onClick={() => setCkOpen(false)} className="opacity-50" data-testid="chat-checkout-close"><X size={14} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input className="rounded-xl border px-3 py-2 text-xs" style={{ borderColor: "var(--border)" }}
                           placeholder={lang === "ar" ? "اسم المستلم" : "Recipient name"} value={ckForm.name}
                           onChange={(e) => setCkForm({ ...ckForm, name: e.target.value })}
                           data-testid="chat-ck-name" />
                    <input className="rounded-xl border px-3 py-2 text-xs" style={{ borderColor: "var(--border)" }} dir="ltr"
                           placeholder={lang === "ar" ? "جوال المستلم" : "Phone"} value={ckForm.phone}
                           onChange={(e) => setCkForm({ ...ckForm, phone: e.target.value })}
                           data-testid="chat-ck-phone" />
                  </div>
                  <textarea className="w-full rounded-xl border px-3 py-2 text-xs resize-none" rows={2}
                            style={{ borderColor: "var(--border)" }}
                            placeholder={lang === "ar" ? "عنوان التوصيل (المنطقة، الشارع، رقم المبنى…)" : "Delivery address"}
                            value={ckForm.address}
                            onChange={(e) => setCkForm({ ...ckForm, address: e.target.value })}
                            data-testid="chat-ck-address" />
                  <div className="flex gap-2">
                    {[["cash", lang === "ar" ? "💵 الدفع عند الاستلام" : "💵 Cash on delivery"],
                      ["card", lang === "ar" ? "💳 بطاقة · Apple Pay" : "💳 Card · Apple Pay"]].map(([v, l]) => (
                      <button key={v} onClick={() => setCkForm({ ...ckForm, payment: v })}
                              className="flex-1 rounded-xl border py-2 text-[11px] font-bold transition-colors"
                              style={ckForm.payment === v
                                ? { background: "var(--primary)", color: "#fff", borderColor: "var(--primary)" }
                                : { borderColor: "var(--border)" }}
                              data-testid={`chat-ck-pay-${v}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                  <button onClick={placeChatOrder} disabled={placing}
                          className="w-full rounded-xl py-2.5 text-xs font-bold text-white"
                          style={{ background: "var(--primary)", opacity: placing ? 0.6 : 1 }}
                          data-testid="chat-checkout-submit">
                    {placing ? (lang === "ar" ? "جارٍ إنشاء طلبك…" : "Placing order…")
                             : (lang === "ar" ? "تأكيد الطلب ✓" : "Confirm order ✓")}
                  </button>
                </div>
              )}

              {/* Input bar — explicit "Send" button styled like ChatGPT.
                  Always-visible (not disabled-styled to invisibility), with a
                  text label on wider screens so it's never missed. */}
              <div className="p-3 flex gap-2 items-end border-t"
                   style={{ borderColor: "var(--border)", background: "#fff" }}>
                <textarea
                  ref={inputRef}
                  className="flex-1 resize-none rounded-2xl border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-700/20 max-h-32"
                  style={{ borderColor: "var(--border)" }}
                  rows={1}
                  placeholder={lang === "ar" ? "اسأل أي شيء..." : "Ask me anything..."}
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = Math.min(e.target.scrollHeight, 128) + "px";
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  data-testid="chatbot-input"
                />
                <button
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  className="rounded-full flex items-center gap-1.5 px-4 py-2.5 font-semibold text-sm shrink-0 transition-all"
                  style={{
                    background: (loading || !input.trim()) ? "var(--bg-subtle)" : "var(--primary)",
                    color: (loading || !input.trim()) ? "var(--text-main)" : "#fff",
                    opacity: (loading || !input.trim()) ? 0.55 : 1,
                    cursor: (loading || !input.trim()) ? "not-allowed" : "pointer",
                  }}
                  data-testid="chatbot-send"
                  aria-label={lang === "ar" ? "إرسال" : "Send"}
                >
                  <Send size={16} className={lang === "ar" ? "rtl-flip" : ""} />
                  <span className="hidden sm:inline">{lang === "ar" ? "إرسال" : "Send"}</span>
                </button>
              </div>
    </>
  );

  // وضع مدمج داخل ويدجت خدمة العملاء الأخضر — مكان واحد لكل شيء
  if (embedded) {
    return (
      <div className="flex-1 min-h-0 h-full flex flex-col bg-white" data-testid="chatbot-embedded">
        {inner}
      </div>
    );
  }

  return (
    <>
      <motion.button
        onClick={() => setOpen(true)}
        className="fixed z-40 rounded-full shadow-2xl flex items-center gap-2 px-4 py-3"
        style={{
          insetInlineStart: 14,
          bottom: "calc(var(--bnav-total, 74px) + 14px)",
          background: "var(--primary)",
          color: "#fff",
        }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        data-testid="chatbot-toggle"
      >
        <Sparkles size={18} />
        <span className="text-sm font-medium">{t("aiChatbot")}</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="chatbot-modal"
          >
            <div className="absolute inset-0 bg-black/40" />
            <motion.div
              className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden"
              style={{ height: "85vh", maxHeight: "720px" }}
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4"
                   style={{ background: "var(--primary)", color: "#fff" }}>
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                       style={{ background: "var(--secondary)", color: "#000" }}>
                    F
                  </div>
                  <div>
                    <div className="font-bold text-base">
                      {lang === "ar" ? "RARE GROUP" : "RARE GROUP"}
                      <span className="ms-2 text-[10px] px-1.5 py-0.5 rounded font-semibold"
                            style={{ background: "var(--secondary)", color: "#000" }}>
                        AI
                      </span>
                    </div>
                    <div className="text-[11px] opacity-80 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 pulse-dot" />
                      {lang === "ar" ? "مساعدك الذكي · جاهز" : "Smart assistant · ready"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {messages.length > 1 && (
                    <button onClick={clearChat} title={lang === "ar" ? "مسح" : "Clear"}
                            data-testid="chatbot-clear"
                            className="p-2 rounded-full hover:bg-white/10 opacity-70">
                      <Trash2 size={16} />
                    </button>
                  )}
                  <button onClick={() => setOpen(false)} data-testid="chatbot-close"
                          className="p-2 rounded-full hover:bg-white/10">
                    <X size={20} />
                  </button>
                </div>
              </div>
              {inner}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* Lightweight markdown renderer — just enough for chat:
 *   **bold**, *italic*, `code`, line breaks, simple bullet lists.
 * No external deps. Safe (no innerHTML on user content).
 */
function Markdown({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  return (
    <>
      {lines.map((line, i) => {
        const isBullet = /^\s*[-*]\s+/.test(line);
        const content = isBullet ? line.replace(/^\s*[-*]\s+/, "") : line;
        const parts = renderInline(content);
        return (
          <div key={`l-${i}-${(line || "").slice(0, 16)}`} className={isBullet ? "flex gap-1.5" : ""}>
            {isBullet && <span className="opacity-60 shrink-0">•</span>}
            <div className="flex-1">{parts}</div>
          </div>
        );
      })}
    </>
  );
}

function renderInline(text) {
  // Tokenize: **bold**, *italic*, `code`. Order matters: bold > italic > code.
  const out = [];
  let cursor = 0;
  const re = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)/g;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > cursor) out.push(<span key={key++}>{text.slice(cursor, m.index)}</span>);
    if (m[1])      out.push(<strong key={key++}>{m[2]}</strong>);
    else if (m[3]) out.push(<em key={key++}>{m[4]}</em>);
    else if (m[5]) out.push(<code key={key++} className="text-xs px-1 py-0.5 rounded" style={{ background: "rgba(0,0,0,0.07)" }}>{m[6]}</code>);
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) out.push(<span key={key++}>{text.slice(cursor)}</span>);
  return out.length ? out : [text];
}
